<template>
  <div class="global-car-info section-card">
    <span class="section-title">{{ title }}</span>

    <el-alert
      v-if="showAlert"
      title="默认规则将应用于所有非单独设置信息的方案"
      type="info"
      :closable="false"
      show-icon
      class="tip-alert"
    />

    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-position="top"
      class="car-info-form"
    >
      <div class="form-row">
        <el-form-item label="车龄" prop="carAge" required>
          <el-select
            v-model="formData.carAge"
            placeholder="请选择"
            clearable
            @change="handleCarAgeChange"
          >
            <el-option
              v-for="item in carAgeOptions"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="表显里程" prop="mileage" required>
          <el-select
            v-model="formData.mileage"
            placeholder="请选择"
            clearable
            @change="handleMileageChange"
          >
            <el-option
              v-for="item in mileageOptions"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="车证" class="checkbox-item">
          <el-checkbox
            v-model="formData.hasLicense"
            @change="handleLicenseChange"
          >
            有车证
          </el-checkbox>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useBatchSession } from '../hooks/useBatchSession'

const props = defineProps({
  // 标题
  title: {
    type: String,
    default: '配置默认车型信息',
  },
  // 是否显示提示
  showAlert: {
    type: Boolean,
    default: true,
  },
  // 是否实时同步到 Vuex（批量模式需要，单个模式不需要）
  syncToStore: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['carAgeChange', 'mileageChange', 'licenseChange'])

const {
  globalCarInfo,
  setCarAge: setCarAgeToStore,
  setMileage,
  setHasLicense,
  carAgeOptions,
  mileageOptions,
  fetchEnums,
} = useBatchSession()

const formRef = ref(null)
const prevCarAge = ref(null)

// 表单数据
const formData = reactive({
  carAge: null,
  mileage: null,
  hasLicense: false,
})

// 自定义校验器（支持 0 作为有效值）
const requiredValidator = (message) => ({
  validator: (_rule, value, callback) => {
    if (value === null || value === undefined || value === '') {
      callback(new Error(message))
    } else {
      callback()
    }
  },
  trigger: 'change',
})

// 表单校验规则
const formRules = {
  carAge: [requiredValidator('请选择车龄')],
  mileage: [requiredValidator('请选择表显里程')],
}

// 车龄变更
const handleCarAgeChange = (val) => {
  if (props.syncToStore) {
    setCarAgeToStore(val)
  }
  emit('carAgeChange', val, prevCarAge.value)
  prevCarAge.value = val
}

// 表显里程变更
const handleMileageChange = (val) => {
  if (props.syncToStore) {
    setMileage(val)
  }
  emit('mileageChange', val)
}

// 车证变更
const handleLicenseChange = (val) => {
  if (props.syncToStore) {
    setHasLicense(val)
  }
  emit('licenseChange', val)
}

// 外部设置车龄（用于取消确认时恢复）
const setCarAge = (val) => {
  formData.carAge = val
  prevCarAge.value = val
}

// 校验
const validate = async () => {
  await formRef.value?.validate()
}

// 获取表单数据
const getFormData = () => {
  return { ...formData }
}

onMounted(() => {
  // 从 Vuex 加载枚举数据（带缓存）
  fetchEnums()

  // 从 Vuex 恢复之前填写的数据
  if (globalCarInfo.value) {
    formData.carAge = globalCarInfo.value.carAge
    formData.mileage = globalCarInfo.value.mileage
    formData.hasLicense = globalCarInfo.value.hasLicense || false
    prevCarAge.value = globalCarInfo.value.carAge
  }
})

defineExpose({
  validate,
  setCarAge,
  getFormData,
})
</script>

<style lang="scss" scoped>
.global-car-info {
  .tip-alert {
    padding: 8px 12px;
  }

  .car-info-form {
    .form-row {
      display: flex;
      flex-direction: row;
      padding: 0px;
      gap: 16px;
      align-self: stretch;
    }

    ::v-deep .el-form-item {
      margin-bottom: 16px;

      .el-form-item__label {
        justify-content: flex-start;
      }

      .el-select {
        width: 240px;
      }
    }
  }
}
</style>
